/**
 * A fixed-capacity bag of coloured line segments, rewritten every frame without allocating:
 * what every debug layer except the nav grid draws with. One `LineSegments`, one draw call.
 * Lines are unlit and MRT-patched like the weather (`patchMrtUnlit`), drawn over everything
 * (no depth test, last in the frame) - a vision cone you cannot see behind a tree is no use.
 */

import { BufferAttribute, BufferGeometry, type Color, LineBasicMaterial, LineSegments } from 'three';
import { patchMrtUnlit } from '../materials/toon';

const TAU = Math.PI * 2;

export class LineBuffer {
  readonly lines: LineSegments;
  private readonly positions: Float32Array;
  private readonly colors: Float32Array;
  private used = 0;

  /** `capacity` is in segments; anything past it is silently dropped. */
  constructor(private readonly capacity: number, name: string) {
    this.positions = new Float32Array(capacity * 6);
    this.colors = new Float32Array(capacity * 6);
    const geometry = new BufferGeometry();
    geometry.setAttribute('position', new BufferAttribute(this.positions, 3));
    geometry.setAttribute('color', new BufferAttribute(this.colors, 3));
    geometry.setDrawRange(0, 0);
    const material = new LineBasicMaterial({ vertexColors: true, depthTest: false, depthWrite: false });
    material.onBeforeCompile = patchMrtUnlit;
    material.customProgramCacheKey = () => 'lines-mrt';
    this.lines = new LineSegments(geometry, material);
    this.lines.frustumCulled = false;
    this.lines.renderOrder = 10;
    this.lines.name = `debug-${name}`;
  }

  /** Segments written since `begin`. */
  get count(): number {
    return this.used;
  }

  begin(): void {
    this.used = 0;
  }

  segment(ax: number, ay: number, az: number, bx: number, by: number, bz: number, c: Color): void {
    if (this.used >= this.capacity) return;
    const o = this.used * 6;
    this.positions[o] = ax;
    this.positions[o + 1] = ay;
    this.positions[o + 2] = az;
    this.positions[o + 3] = bx;
    this.positions[o + 4] = by;
    this.positions[o + 5] = bz;
    for (let k = 0; k < 6; k += 3) {
      this.colors[o + k] = c.r;
      this.colors[o + k + 1] = c.g;
      this.colors[o + k + 2] = c.b;
    }
    this.used++;
  }

  /** Arc of a horizontal circle from angle `from` to `to` (sim angles: 0 = +z). `every` > 1 leaves gaps: dashes. */
  arc(
    x: number, y: number, z: number, r: number, from: number, to: number, steps: number, c: Color, every = 1,
  ): void {
    for (let i = 0; i < steps; i++) {
      if (i % every !== 0) continue;
      const a0 = from + ((to - from) * i) / steps;
      const a1 = from + ((to - from) * (i + 1)) / steps;
      this.segment(x + Math.sin(a0) * r, y, z + Math.cos(a0) * r, x + Math.sin(a1) * r, y, z + Math.cos(a1) * r, c);
    }
  }

  circle(x: number, y: number, z: number, r: number, steps: number, c: Color, every = 1): void {
    this.arc(x, y, z, r, 0, TAU, steps, c, every);
  }

  /** Axis-aligned rectangle on the horizontal plane at height `y`. */
  rect(x: number, y: number, z: number, hx: number, hz: number, c: Color): void {
    this.segment(x - hx, y, z - hz, x + hx, y, z - hz, c);
    this.segment(x + hx, y, z - hz, x + hx, y, z + hz, c);
    this.segment(x + hx, y, z + hz, x - hx, y, z + hz, c);
    this.segment(x - hx, y, z + hz, x - hx, y, z - hz, c);
  }

  end(): void {
    this.lines.geometry.setDrawRange(0, this.used * 2);
    this.lines.geometry.getAttribute('position').needsUpdate = true;
    this.lines.geometry.getAttribute('color').needsUpdate = true;
  }

  dispose(): void {
    this.lines.removeFromParent();
    this.lines.geometry.dispose();
    const material = this.lines.material;
    for (const m of Array.isArray(material) ? material : [material]) m.dispose();
  }
}
